/*
 * assets/wubs-expert.js (v0.1 – SHELL)
 * Isolated custom element for the Expert Hub.
 * - Hash-router (#overview #products #resources #referral #account)
 * - Back/forward + keyboard via real <a> links and hashchange
 * - Copy-knappar med screen reader-feedback
 * Ingen proxy-data i skalet. Skyddat mot dubbeldefinition (Theme Editor reload).
 */
(function () {
  'use strict';

  /* Tabs are section blocks, so the merchant can reorder, rename or remove them
     in the theme editor. The valid view list is therefore read from the DOM at
     connect time — hardcoding it silently breaks any tab added later (that is
     exactly what happened to the Customer Service tab). This list is only the
     fallback for when the markup hasn't rendered yet. */
  var FALLBACK_VIEWS = ['overview', 'products', 'resources', 'webinars', 'articles', 'community', 'norsan', 'referral', 'support', 'account'];
  var DEFAULT_VIEW = 'overview';

  if (customElements.get('wubs-expert-hub')) return;

  customElements.define(
    'wubs-expert-hub',
    class WubsExpertHub extends HTMLElement {
      constructor() {
        super();
        this._onHashChange = this._onHashChange.bind(this);
        this._onClick = this._onClick.bind(this);
        this._userNavigated = false;
      }

      connectedCallback() {
        this.live = this.querySelector('[data-wx-live]');
        this.navLinks = Array.prototype.slice.call(this.querySelectorAll('[data-wx-tab]'));
        this.views = Array.prototype.slice.call(this.querySelectorAll('[data-wx-view]'));
        this._viewNames = this.views
          .map(function (v) { return v.dataset.wxView; })
          .filter(Boolean);
        if (!this._viewNames.length) this._viewNames = FALLBACK_VIEWS.slice();

        this._loadStrings();
        this._mountNavInHeader();

        window.addEventListener('hashchange', this._onHashChange);
        this.addEventListener('click', this._onClick);
        this._attachments = {};
        this._onFileChange = this._onFileChange.bind(this);
        this.addEventListener('change', this._onFileChange);

        this.activate(this._viewFromHash(), false);
        this.loadData();
        this._initOrderCart();
        this._initWebinarBanner();
        this._renderWebinarTimes();
      }

      disconnectedCallback() {
        window.removeEventListener('hashchange', this._onHashChange);
        this.removeEventListener('click', this._onClick);
        this.removeEventListener('change', this._onFileChange);
        if (this._onNavResize) window.removeEventListener('resize', this._onNavResize);
      }

      /* Webinar times are authored in US Eastern (the app forces ET on input).
         Show ET and — when the section asks for it — Pacific alongside it, so an
         expert on either coast reads their own time without doing the math.
         Intl handles DST, so ET/PT print as EST/EDT and PST/PDT automatically. */
      /* UI copy comes from locales/en.default.json ("wubs" namespace), rendered
         into the page as JSON by the section. Falls back to the key's built-in
         English so a missing translation never blanks the interface. */
      _loadStrings() {
        this._i18n = {};
        try {
          var el = document.querySelector('[data-wx-i18n]');
          if (el) this._i18n = JSON.parse(el.textContent) || {};
        } catch (e) { this._i18n = {}; }
      }

      _t(key, fallback, vars) {
        var out = (this._i18n && this._i18n[key]) || fallback || '';
        if (vars) {
          Object.keys(vars).forEach(function (k) {
            out = out.replace(new RegExp('\\{\\{\\s*' + k + '\\s*\\}\\}', 'g'), vars[k]);
          });
        }
        return out;
      }

      _renderWebinarTimes() {
        var nodes = this.querySelectorAll('[data-wx-when]');
        if (!nodes.length || typeof Intl === 'undefined' || !Intl.DateTimeFormat) return;
        var showPt = this.dataset.showPt !== '0';

        function fmt(date, tz) {
          try {
            var parts = new Intl.DateTimeFormat('en-US', {
              timeZone: tz, month: 'short', day: 'numeric', year: 'numeric',
              hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
            }).formatToParts(date);
            var get = function (t) {
              for (var i = 0; i < parts.length; i++) if (parts[i].type === t) return parts[i].value;
              return '';
            };
            return {
              date: get('month') + ' ' + get('day') + ', ' + get('year'),
              time: get('hour') + ':' + get('minute') + ' ' + get('dayPeriod'),
              zone: get('timeZoneName')
            };
          } catch (e) { return null; }
        }

        for (var i = 0; i < nodes.length; i++) {
          var el = nodes[i];
          var raw = el.getAttribute('data-wx-when');
          if (!raw) continue;
          var d = new Date(raw);
          if (isNaN(d.getTime())) continue;

          var et = fmt(d, 'America/New_York');
          if (!et) continue;
          var mins = el.getAttribute('data-wx-mins');
          var line = et.date + ' \u00b7 ' + et.time + ' ' + et.zone;
          if (mins) line += ' \u00b7 ' + mins + ' min';

          el.textContent = line;
          if (showPt) {
            var pt = fmt(d, 'America/Los_Angeles');
            if (pt) {
              var alt = document.createElement('span');
              alt.className = 'wx-web-tz';
              /* Same instant on the West Coast — include the date only when it differs. */
              alt.textContent = (pt.date === et.date ? '' : pt.date + ' \u00b7 ') + pt.time + ' ' + pt.zone;
              el.appendChild(alt);
            }
          }
        }
      }

      _onFileChange(e) {
        if (!e.target || !e.target.closest) return;
        var variant = e.target.closest('[data-wx-variant]');
        if (variant) { this._onVariantChange(variant); return; }
        var input = e.target.closest('[data-wx-attach-input]');
        if (input) this._attachUpload(input);
      }

      /* The row's price and tier chips are rendered by Liquid from the default
         variant. Repaint them from the chosen variant so the ladder on screen is
         the one that will actually be applied. */
      _onVariantChange(sel) {
        var row = sel.closest('[data-wx-prow]');
        if (!row || !sel.options.length) return;
        var opt = sel.options[sel.selectedIndex];
        if (!opt) return;

        var price = parseInt(opt.getAttribute('data-price'), 10);
        if (isNaN(price)) return;

        var priceEl = row.querySelector('.wx-order-price');
        if (priceEl) priceEl.innerHTML = '<span>' + this._formatMoney(price) + '</span>';

        var breaks = [];
        try { breaks = JSON.parse(opt.getAttribute('data-breaks') || '[]'); } catch (e) { breaks = []; }

        var tiers = row.querySelector('.wx-tiers');
        if (!tiers) return;
        if (!breaks.length) { tiers.hidden = true; tiers.innerHTML = ''; return; }
        tiers.hidden = false;

        var self = this;
        var html = '<span class="wx-tier"><span class="wx-tier-q">1\u2013' + (breaks[0].q - 1) + '</span>' + this._formatMoney(price) + '</span>';
        breaks.forEach(function (b, i) {
          var last = i === breaks.length - 1;
          var range = last ? b.q + '+' : b.q + '\u2013' + (breaks[i + 1].q - 1);
          html += '<span class="wx-tier' + (last ? ' wx-tier--best' : '') + '"><span class="wx-tier-q">' +
                  range + '</span>' + self._formatMoney(b.p) + '</span>';
        });
        tiers.innerHTML = html;
      }

      /* Relocate the hub tabs into the store header's (empty) middle slot so they
         sit at the same height as the logo/icons, and move "Back to store" into
         the header's end icons. Store menu is hidden via CSS. Scoped to this page
         only (the custom element renders on the Expert Hub page). Falls back to
         the in-section bar if the header markup isn't found. */
      _mountNavInHeader() {
        try {
          var header = document.querySelector('header.header');
          var nav = this.querySelector('.wx-nav');
          var side = this.querySelector('.wx-side');
          if (!header || !nav) return;
          // Place the tabs INTO the header itself (absolutely positioned via CSS
          // in its empty middle). We avoid the theme's .header__navigation cell:
          // it's on its own compositing layer for the mega-menu and won't render
          // our injected content.
          if (window.getComputedStyle(header).position === 'static') {
            header.style.position = 'relative';
          }
          // Keep "Back to store" as the last item in the tab row (after Profile).
          var back = this.querySelector('.wx-back');
          if (back) { nav.appendChild(back); }

          this._navHeader = header;
          this._navSide = side;
          this._placeNav();

          this._onNavResize = this._debounce(this._placeNav.bind(this), 150);
          window.addEventListener('resize', this._onNavResize);
        } catch (e) { /* keep the fallback bar */ }
      }

      /* In the header the tabs only get `calc(100% - 460px)` — the space left
         between the logo and the icons. Ten tabs need ~1345px, which does not
         fit on any laptop-width window, and macOS hides the overflow scrollbar,
         so tabs simply vanish with nothing to say they exist. Measure first and
         fall back to the full-width bar under the header when they don't fit. */
      _placeNav() {
        var nav = this.querySelector('.wx-nav');
        var header = this._navHeader;
        var side = this._navSide;
        if (!nav || !header) return;

        if (nav.parentNode !== header) {
          nav.classList.add('wx-hdr-nav');
          header.appendChild(nav);
        }
        if (side) side.hidden = true;

        // scrollWidth > clientWidth means at least one tab is cut off.
        nav.classList.remove('wx-nav--compact', 'wx-nav--tight');
        if (nav.scrollWidth > nav.clientWidth + 1) {
          nav.classList.remove('wx-hdr-nav');
          if (side) {
            side.hidden = false;
            side.appendChild(nav);
          }
          // Full width still isn't enough on narrow laptops — tighten the tabs,
          // then drop the icons rather than let a label disappear.
          if (nav.scrollWidth > nav.clientWidth + 1) {
            nav.classList.add('wx-nav--compact');
            if (nav.scrollWidth > nav.clientWidth + 1) {
              nav.classList.add('wx-nav--tight');
            }
          }
        }
      }

      _debounce(fn, wait) {
        var t;
        return function () {
          var args = arguments, self = this;
          clearTimeout(t);
          t = setTimeout(function () { fn.apply(self, args); }, wait);
        };
      }

      _viewFromHash() {
        var h = (window.location.hash || '').replace('#', '').toLowerCase();
        var known = this._viewNames || FALLBACK_VIEWS;
        return known.indexOf(h) !== -1 ? h : (this.dataset.defaultView || known[0] || DEFAULT_VIEW);
      }

      _onHashChange() {
        this._userNavigated = true;
        this.activate(this._viewFromHash(), true);
      }

      _onClick(e) {
        var copyBtn = e.target.closest('[data-wubs-copy]');
        if (copyBtn) { e.preventDefault(); this.copy(copyBtn); return; }

        var qstep = e.target.closest('[data-wx-qstep]');
        if (qstep) { e.preventDefault(); this._rowQtyStep(qstep); return; }

        var addBtn = e.target.closest('[data-wx-add]');
        if (addBtn) { e.preventDefault(); this._addFromRow(addBtn); return; }

        var addCart = e.target.closest('[data-wx-addcart]');
        if (addCart) { e.preventDefault(); this._addResourceToCart(addCart); return; }

        var step = e.target.closest('[data-wx-step]');
        if (step) { e.preventDefault(); this._cartStep(step.getAttribute('data-vid'), parseInt(step.getAttribute('data-wx-step'), 10)); return; }

        var rm = e.target.closest('[data-wx-remove]');
        if (rm) { e.preventDefault(); this._cartRemove(rm.getAttribute('data-vid')); return; }

        var co = e.target.closest('[data-wx-checkout]');
        if (co) { e.preventDefault(); this._checkout(co); return; }

        var cf = e.target.closest('[data-wx-catfilter]');
        if (cf) { e.preventDefault(); this._filterResources(cf); return; }

        var an = e.target.closest('[data-wx-addr-new]');
        if (an) { e.preventDefault(); this._addrFormOpen(null); return; }
        var ae = e.target.closest('[data-wx-addr-edit]');
        if (ae) { e.preventDefault(); this._addrFormOpen(this._addrById(ae.getAttribute('data-wx-addr-edit'))); return; }
        var ac = e.target.closest('[data-wx-addr-cancel]');
        if (ac) { e.preventDefault(); this._addrFormClose(); return; }
        var asv = e.target.closest('[data-wx-addr-save]');
        if (asv) { e.preventDefault(); this._addrSave(asv); return; }
        var adl = e.target.closest('[data-wx-addr-del]');
        if (adl) { e.preventDefault(); this._addrDelete(adl.getAttribute('data-wx-addr-del')); return; }
        var adf = e.target.closest('[data-wx-addr-default]');
        if (adf) { e.preventDefault(); this._addrSetDefault(adf.getAttribute('data-wx-addr-default')); return; }

        var apick = e.target.closest('[data-wx-attach-pick]');
        if (apick) { e.preventDefault(); this._attachPick(apick); return; }
        var arem = e.target.closest('[data-wx-attach-remove]');
        if (arem) { e.preventDefault(); this._attachRemove(arem); return; }

        var ce = e.target.closest('[data-wx-code-edit]');
        if (ce) { e.preventDefault(); this._codeFormOpen(); return; }
        var cc = e.target.closest('[data-wx-code-cancel]');
        if (cc) { e.preventDefault(); this._codeFormClose(); return; }
        var cs = e.target.closest('[data-wx-code-save]');
        if (cs) { e.preventDefault(); this._codeSave(cs); return; }

        var ics = e.target.closest('[data-wx-ics]');
        if (ics) { e.preventDefault(); this._downloadIcs(ics); return; }
        var wbx = e.target.closest('[data-wx-web-dismiss]');
        if (wbx) { e.preventDefault(); this._dismissWebinarBanner(wbx.getAttribute('data-wx-web-dismiss')); return; }

        var artsub = e.target.closest('[data-wx-art-submit]');
        if (artsub) { e.preventDefault(); this._submitArticle(artsub); return; }

        var fpost = e.target.closest('[data-wx-feed-post]');
        if (fpost) { e.preventDefault(); this._feedPost(fpost.getAttribute('data-wx-feed-post')); return; }
        var fcom = e.target.closest('[data-wx-feed-comment]');
        if (fcom) { e.preventDefault(); this._feedComment(fcom); return; }
        var fmore = e.target.closest('[data-wx-feed-more]');
        if (fmore) { e.preventDefault(); this._feedMore(fmore.getAttribute('data-wx-feed-more')); return; }
      }

      _filterResources(btn) {
        var cat = btn.getAttribute('data-wx-catfilter');
        this.querySelectorAll('[data-wx-catfilter]').forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
        this.querySelectorAll('.wx-res').forEach(function (card) {
          card.hidden = !(cat === 'all' || card.getAttribute('data-category') === cat);
        });
      }

      activate(view, focusPanel) {
        var known = this._viewNames || FALLBACK_VIEWS;
        if (known.indexOf(view) === -1) view = this.dataset.defaultView || known[0] || DEFAULT_VIEW;

        this.views.forEach(function (panel) {
          panel.classList.toggle('is-active', panel.dataset.wxView === view);
        });

        this.navLinks.forEach(function (link) {
          var active = link.dataset.wxTab === view;
          if (active) {
            link.setAttribute('aria-current', 'page');
            if (link.parentElement && typeof link.offsetLeft === 'number') {
              var navBar = link.closest('.wx-nav');
              if (navBar && navBar.scrollWidth > navBar.clientWidth) {
                navBar.scrollTo({ left: link.offsetLeft - (navBar.clientWidth - link.offsetWidth) / 2, behavior: 'smooth' });
              }
            }
          } else {
            link.removeAttribute('aria-current');
          }
        });

        if (view === 'community' || view === 'norsan') this._loadFeed(view);

        if (focusPanel && this._userNavigated) {
          var active = this.querySelector('[data-wx-view="' + view + '"]');
          if (active) active.focus({ preventScroll: true });
        }
      }

      copy(btn) {
        var text = btn.getAttribute('data-wubs-copy') || '';
        var label = btn.getAttribute('data-wubs-copy-label') || 'value';
        var self = this;

        var done = function () { self._copyFeedback(btn, label); };
        var fail = function () { self._announce(this._t('could_not_copy', 'Could not copy') + ' ' + label); };

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done, function () {
            self._legacyCopy(text) ? done() : fail();
          });
        } else {
          this._legacyCopy(text) ? done() : fail();
        }
      }

      _legacyCopy(text) {
        try {
          var ta = document.createElement('textarea');
          ta.value = text;
          ta.setAttribute('readonly', '');
          ta.style.position = 'absolute';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          var ok = document.execCommand('copy');
          document.body.removeChild(ta);
          return ok;
        } catch (err) {
          return false;
        }
      }

      _copyFeedback(btn, label) {
        var target = btn.querySelector('.btn-text') || btn;
        var original = btn.dataset._orig || target.textContent.trim();
        btn.dataset._orig = original;
        target.textContent = this._t('copied', 'Copied');
        this._announce(label.charAt(0).toUpperCase() + label.slice(1) + ' copied to clipboard');
        clearTimeout(btn._t);
        btn._t = setTimeout(function () { target.textContent = original; }, 1600);
      }

      _announce(msg) {
        if (this.live) this.live.textContent = msg;
      }

      /* ---------- Live data (App Proxy) ---------- */
      loadData() {
        if (this.dataset.loggedIn !== '1') return; // preview / not logged in → keep placeholders
        var base = (this.dataset.proxy || '/apps/norsan-expert').replace(/\/$/, '');
        var self = this;
        this.setAttribute('data-wx-loading', '');
        self._loadAddresses();
        self._loadArticles();

        var getJSON = function (path) {
          return fetch(base + path, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            cache: 'no-store'
          }).then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
          });
        };

        var mePromise = getJSON('/goaffpro/me').then(
          function (data) {
            if (data && data.authenticated && data.expert) {
              self.renderMe(data.expert);
            } else {
              self.notice(self._t('err_not_expert', 'Your account isn’t linked as a Norsan Expert yet.'));
            }
          },
          function () { self.notice(self._t('err_profile_load', 'We couldn’t load your expert profile. Please refresh.'), 'error'); }
        );

        var statsPromise = getJSON('/goaffpro/stats').then(
          function (stats) { self.renderStats(stats || {}); },
          function () { self.renderStats({}); } // leave skeletons; not fatal
        );

        var earningsPromise = Promise.all([
          getJSON('/goaffpro/orders').catch(function () { return {}; }),
          getJSON('/goaffpro/payouts').catch(function () { return {}; })
        ]).then(function (res) { self.renderEarnings(res[0], res[1]); });

        Promise.all([mePromise, statsPromise, earningsPromise]).then(function () {
          self.removeAttribute('data-wx-loading');
        });
      }

      renderEarnings(orders, payouts) {
        var commission = (orders && orders.aggregate && orders.aggregate.affiliate_commission) || 0;
        var paid = (payouts && payouts.paidAmount) || 0;
        var pending = Math.max(0, commission - paid);
        this._setEarn('commission', commission);
        this._setEarn('paid', paid);
        this._setEarn('pending', pending);
      }

      _setEarn(key, dollars) {
        var self = this;
        this.querySelectorAll('[data-wx-earn="' + key + '"]').forEach(function (el) {
          el.textContent = self._moneyDollars(dollars);
          el.classList.remove('wx-skel');
        });
      }

      _moneyDollars(v) {
        return '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }

      renderMe(e) {
        this._setText('[data-wx-field="first-name"]', e.first_name);
        this._setText('[data-wx-field="company-name"]', e.company_name);
        this._setText('[data-wx-field="company-location"]', e.company_location);
        this._setText('[data-wx-field="status"]', this._statusLabel(e.status));
        this._setReferral('link', e.referral_link);
        this._setReferral('code', e.referral_code);
      }

      renderStats(s) {
        this._setKpi('clicks', this._num(s.clicks));
        this._setKpi('orders', this._num(s.orders));
        this._setKpi('revenue', s.revenue_formatted || this._num(s.revenue));
        this._setKpi('conversion', (s.conversion_rate || s.conversion_rate === 0) ? s.conversion_rate + '%' : '—');
      }

      _statusLabel(status) {
        var map = { active: 'Active Expert', pending: 'Pending review', paused: 'Paused', rejected: 'Inactive' };
        return map[status] || 'Expert';
      }

      _num(v) {
        if (v === null || v === undefined || v === '') return '0';
        var n = Number(v);
        return isNaN(n) ? String(v) : n.toLocaleString('en-US');
      }

      _setText(sel, val) {
        if (val === null || val === undefined || val === '') return;
        this.querySelectorAll(sel).forEach(function (el) { el.textContent = val; });
      }

      _setKpi(key, val) {
        this.querySelectorAll('[data-wx-kpi="' + key + '"]').forEach(function (el) {
          el.textContent = val;
          el.classList.remove('wx-skel');
        });
      }

      _setReferral(kind, value) {
        if (!value) return;
        if (kind === 'link') {
          var shopUrl = (this.dataset.shopUrl || '').replace(/\/$/, '');
          if (shopUrl) value = value.replace(/https?:\/\/[^/]*\.myshopify\.com/i, shopUrl);
        }
        this.querySelectorAll('[data-wx-ref="' + kind + '"]').forEach(function (el) {
          el.textContent = value;
        });
        this.querySelectorAll('[data-wx-copy="' + kind + '"]').forEach(function (btn) {
          btn.setAttribute('data-wubs-copy', value);
          btn.removeAttribute('disabled');
        });
        if (kind === 'link') this._renderQr(value);
      }

      _renderQr(url) {
        if (typeof qrcode === 'undefined' || !url) return;
        var box = this.querySelector('[data-wx-qr]');
        var imgWrap = this.querySelector('[data-wx-qr-img]');
        if (!box || !imgWrap) return;
        try {
          var qr = qrcode(0, 'M');
          qr.addData(url);
          qr.make();
          var svg = qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
          imgWrap.innerHTML = svg;
          box.hidden = false;
          var dl = this.querySelector('[data-wx-qr-dl]');
          if (dl) {
            if (dl._url) URL.revokeObjectURL(dl._url);
            dl._url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
            dl.href = dl._url;
          }
        } catch (e) { /* QR is a non-critical enhancement */ }
      }

      /* ---------- Referral code editing ---------- */
      _codeFormOpen() {
        var form = this.querySelector('[data-wx-code-form]');
        if (!form) return;
        var input = form.querySelector('[data-wx-code-input]');
        var current = this.querySelector('[data-wx-ref="code"]');
        if (input) {
          var val = current ? (current.textContent || '').trim() : '';
          input.value = (val && val !== '—') ? val : '';
        }
        this._codeMsg('', null);
        form.hidden = false;
        var btn = this.querySelector('[data-wx-code-edit]');
        if (btn) btn.hidden = true;
        if (input) { input.focus(); input.select(); }
      }

      _codeFormClose() {
        var form = this.querySelector('[data-wx-code-form]');
        if (form) form.hidden = true;
        var btn = this.querySelector('[data-wx-code-edit]');
        if (btn) btn.hidden = false;
        this._codeMsg('', null);
      }

      _codeMsg(text, kind) {
        var el = this.querySelector('[data-wx-code-msg]');
        if (!el) return;
        el.classList.remove('wx-codeform-msg--err', 'wx-codeform-msg--ok');
        if (!text) { el.hidden = true; el.textContent = ''; return; }
        if (kind) el.classList.add('wx-codeform-msg--' + kind);
        el.textContent = text;
        el.hidden = false;
      }

      _codeSave(btn) {
        var form = this.querySelector('[data-wx-code-form]');
        var input = form ? form.querySelector('[data-wx-code-input]') : null;
        if (!input) return;
        var code = (input.value || '').trim().toUpperCase();
        if (!/^[A-Z0-9][A-Z0-9-]{2,19}$/.test(code)) {
          this._codeMsg(this._t('code_invalid', 'Use 3–20 characters — letters, numbers or hyphens, not starting with a hyphen.'), 'err');
          input.focus();
          return;
        }
        var self = this;
        var label = btn.querySelector('.btn-text') || btn;
        var prev = label.textContent;
        btn.setAttribute('disabled', '');
        label.textContent = 'Saving…';
        this._codeMsg('', null);
        this._pfetch('/goaffpro/code', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ code: code })
        }).then(function (r) {
          return r.json().catch(function () { return { ok: false, error: self._t('err_unexpected', 'Unexpected server response.') }; });
        }).then(function (d) {
          if (!d || !d.ok) throw new Error((d && d.error) || self._t('code_failed', 'Could not update your code.'));
          self._setReferral('code', d.referral_code);
          self._setReferral('link', d.referral_link);
          self._codeMsg('Saved — your referral link now uses ' + d.referral_code + '.', 'ok');
          self._announce(self._t('code_updated', 'Referral code updated'));
          window.setTimeout(function () { self._codeFormClose(); }, 2200);
        }).catch(function (err) {
          self._codeMsg(err.message || self._t('code_failed', 'Could not update your code.'), 'err');
        }).then(function () {
          btn.removeAttribute('disabled');
          label.textContent = prev;
        });
      }

      _addResourceToCart(btn) {
        var vid = btn.getAttribute('data-vid');
        if (!vid || btn.hasAttribute('disabled')) return;
        var self = this;
        var label = btn.textContent;
        btn.setAttribute('disabled', '');
        btn.textContent = 'Adding…';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(vid), quantity: 1 }] })
        }).then(function (r) {
          if (!r.ok) throw new Error('add_failed');
          window.location.href = '/cart';
        }).catch(function () {
          btn.removeAttribute('disabled');
          btn.textContent = label;
          self.notice(self._t('order_add_failed', 'Could not add this item to your cart. Please try again.'), 'error');
        });
      }

      /* ---------- Profile: shipping addresses ---------- */
      _pbase() { return (this.dataset.proxy || '/apps/norsan-expert').replace(/\/$/, ''); }

      /* All App Proxy traffic goes through here so a cold app doesn't surface
         as an error to the expert.

         The app sleeps when idle; the first request after that comes back 5xx
         with an HTML/plain-text body — produced before any of our handlers run.
         Our own endpoints always answer with JSON, so a non-JSON 5xx is a
         reliable "the app wasn't awake yet" signal, and replaying the request
         cannot duplicate a write. A 5xx that IS JSON is a real error from our
         code and gets passed straight through, retried or not. */
      _pfetch(path, opts) {
        var url = this._pbase() + path;
        var maxAttempts = 3;
        var delays = [600, 1600];

        function run(attempt) {
          return fetch(url, opts).then(function (res) {
            if (res.status < 500 || attempt >= maxAttempts) return res;
            return res.text().then(function (text) {
              var isOurJson = true;
              try { JSON.parse(text); } catch (e) { isOurJson = false; }
              if (isOurJson) {
                // A real error from our API — hand it back intact.
                return new Response(text, { status: res.status, headers: res.headers });
              }
              return new Promise(function (resolve) {
                window.setTimeout(function () { resolve(run(attempt + 1)); }, delays[attempt - 1] || 1600);
              });
            });
          }).catch(function (err) {
            // Network-level failure (app not reachable yet) — same treatment.
            if (attempt >= maxAttempts) throw err;
            return new Promise(function (resolve) {
              window.setTimeout(function () { resolve(run(attempt + 1)); }, delays[attempt - 1] || 1600);
            });
          });
        }
        return run(1);
      }
      _addrById(id) { return (this._addrCache || []).filter(function (a) { return String(a.id) === String(id); })[0] || null; }

      _loadAddresses() {
        if (this.dataset.loggedIn !== '1') return;
        var self = this;
        this._pfetch('/addresses', { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) {
            self._addrDefaultId = (d && d.default_address_id) || null;
            self._renderAddresses(d ? (d.addresses || []) : null);
          })
          .catch(function () { self._renderAddresses(null); });
      }

      _renderAddresses(list) {
        var box = this.querySelector('[data-wx-addr-list]');
        if (!box) return;
        if (list === null) { box.innerHTML = '<p class="wx-empty">We couldn’t load your addresses. Please refresh.</p>'; return; }
        this._addrCache = list;
        if (!list.length) { box.innerHTML = '<p class="wx-empty">No addresses yet. Add one so your orders ship to the right place.</p>'; return; }
        var self = this;
        var esc = function (s) { var d = document.createElement('div'); d.textContent = (s == null ? '' : s); return d.innerHTML; };
        box.innerHTML = list.map(function (a) {
          var isDef = String(a.id) === String(self._addrDefaultId);
          var lines = [
            [a.first_name, a.last_name].filter(Boolean).join(' '),
            a.company, a.address1, a.address2,
            [a.city, (a.province_code || a.province), a.zip].filter(Boolean).join(', '),
            a.country, a.phone
          ].filter(Boolean).map(esc).join('<br>');
          return '<div class="wx-addr">' +
            (isDef ? '<span class="wx-addr-badge">Default</span>' : '') +
            '<div class="wx-addr-body">' + lines + '</div>' +
            '<div class="wx-addr-acts">' +
              '<button type="button" class="wx-addr-link" data-wx-addr-edit="' + esc(a.id) + '">Edit</button>' +
              (isDef ? '' : '<button type="button" class="wx-addr-link" data-wx-addr-default="' + esc(a.id) + '">Make default</button>') +
              '<button type="button" class="wx-addr-link wx-addr-del" data-wx-addr-del="' + esc(a.id) + '">Delete</button>' +
            '</div></div>';
        }).join('');
      }

      _addrFormOpen(addr) {
        var f = this.querySelector('[data-wx-addr-form]');
        if (!f) return;
        var keys = ['id', 'first_name', 'last_name', 'company', 'address1', 'address2', 'city', 'province', 'zip', 'country', 'phone'];
        keys.forEach(function (k) {
          var el = f.querySelector('[data-wx-af="' + k + '"]');
          if (!el) return;
          var v = addr && addr[k] != null ? addr[k] : '';
          if (k === 'province' && addr) v = addr.province || addr.province_code || '';
          if (k === 'country' && !addr) v = 'United States';
          el.value = v;
        });
        var mk = f.querySelector('[data-wx-af="make_default"]');
        if (mk) mk.checked = false;
        var title = f.querySelector('[data-wx-addr-title]');
        if (title) title.textContent = addr ? 'Edit address' : 'Add address';
        f.hidden = false;
        var nb = this.querySelector('[data-wx-addr-new]'); if (nb) nb.hidden = true;
        var first = f.querySelector('[data-wx-af="first_name"]'); if (first) first.focus();
      }

      _addrFormClose() {
        var f = this.querySelector('[data-wx-addr-form]'); if (f) f.hidden = true;
        var nb = this.querySelector('[data-wx-addr-new]'); if (nb) nb.hidden = false;
      }

      _addrSave(btn) {
        var f = this.querySelector('[data-wx-addr-form]'); if (!f) return;
        var g = function (k) { var el = f.querySelector('[data-wx-af="' + k + '"]'); return el ? el.value.trim() : ''; };
        var body = {
          id: g('id') || undefined,
          first_name: g('first_name'), last_name: g('last_name'), company: g('company'),
          address1: g('address1'), address2: g('address2'), city: g('city'),
          province: g('province'), zip: g('zip'), country: g('country'), phone: g('phone'),
          make_default: !!(f.querySelector('[data-wx-af="make_default"]') || {}).checked
        };
        if (!body.first_name || !body.last_name || !body.address1 || !body.city || !body.zip) {
          this.notice(this._t('addr_missing', 'Please fill in name, address, city and ZIP.'), 'error');
          return;
        }
        var self = this;
        btn.setAttribute('disabled', '');
        this._pfetch('/addresses', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        }).then(function (r) { return r.json().catch(function () { return { ok: false }; }); })
          .then(function (d) {
            btn.removeAttribute('disabled');
            if (d && d.ok) { self._addrFormClose(); self._loadAddresses(); }
            else { self.notice((d && d.error) || self._t('addr_save_failed', 'Could not save the address.'), 'error'); }
          })
          .catch(function () { btn.removeAttribute('disabled'); self.notice(self._t('addr_save_failed', 'Could not save the address.'), 'error'); });
      }

      _addrDelete(id) {
        if (!window.confirm(this._t('addr_delete_confirm', 'Delete this address?'))) return;
        var self = this;
        this._pfetch('/addresses/delete', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: id })
        }).then(function () { self._loadAddresses(); })
          .catch(function () { self.notice(self._t('addr_delete_failed', 'Could not delete the address.'), 'error'); });
      }

      _addrSetDefault(id) {
        var a = this._addrById(id); if (!a) return;
        var self = this;
        var body = {
          id: id, first_name: a.first_name, last_name: a.last_name, company: a.company,
          address1: a.address1, address2: a.address2, city: a.city,
          province: a.province_code || a.province, zip: a.zip, country: a.country, phone: a.phone,
          make_default: true
        };
        this._pfetch('/addresses', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }).then(function () { self._loadAddresses(); })
          .catch(function () { self.notice(self._t('addr_default_failed', 'Could not update the default address.'), 'error'); });
      }

      /* ---------- Webinars: .ics + overview banner ---------- */
      _downloadIcs(btn) {
        var start = new Date(btn.getAttribute('data-ics-start'));
        if (isNaN(start.getTime())) return;
        var mins = parseInt(btn.getAttribute('data-ics-mins'), 10) || 60;
        var end = new Date(start.getTime() + mins * 60000);
        var title = btn.getAttribute('data-ics-title') || this._t('web_ics_title', 'NORSAN Webinar');
        var url = btn.getAttribute('data-ics-url') || '';
        var desc = btn.getAttribute('data-ics-desc') || '';
        var fmt = function (d) { return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, ''); };
        var esc = function (s) { return String(s || '').replace(/([,;\\])/g, '\\$1').replace(/\r?\n/g, '\\n'); };
        var lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//NORSAN//Expert Hub//EN', 'CALSCALE:GREGORIAN', 'BEGIN:VEVENT',
          'UID:norsan-' + start.getTime() + '@norsan-health.com', 'DTSTAMP:' + fmt(new Date()),
          'DTSTART:' + fmt(start), 'DTEND:' + fmt(end), 'SUMMARY:' + esc(title),
          'DESCRIPTION:' + esc(desc + (url ? '\n\nJoin: ' + url : ''))];
        if (url) lines.push('URL:' + esc(url));
        lines.push('END:VEVENT', 'END:VCALENDAR');
        var blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
        var a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'norsan-webinar.ics';
        document.body.appendChild(a); a.click();
        var href = a.href;
        setTimeout(function () { URL.revokeObjectURL(href); a.remove(); }, 200);
      }

      _initWebinarBanner() {
        var cards = this.querySelectorAll('[data-wx-webinar-upcoming]');
        if (!cards.length) return;
        var soonest = null, soonestTs = Infinity;
        cards.forEach(function (c) {
          var ts = Date.parse(c.getAttribute('data-start'));
          if (!isNaN(ts) && ts < soonestTs) { soonestTs = ts; soonest = c; }
        });
        if (!soonest) return;
        try { if (window.localStorage.getItem('wxWebinarDismiss:' + soonestTs) === '1') return; } catch (e) {}
        var overview = this.querySelector('[data-wx-view="overview"]');
        if (!overview) return;
        var title = soonest.getAttribute('data-title') || 'Upcoming webinar';
        var join = soonest.getAttribute('data-join') || '';
        var mins = soonest.getAttribute('data-mins') || '60';
        var desc = soonest.getAttribute('data-desc') || '';
        var startIso = soonest.getAttribute('data-start');
        var dateStr = new Date(soonestTs).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
        var esc = function (s) { var d = document.createElement('div'); d.textContent = (s == null ? '' : s); return d.innerHTML; };
        var banner = document.createElement('div');
        banner.className = 'wx-web-banner';
        banner.innerHTML =
          '<div class="wx-web-banner-txt">Upcoming webinar: <strong>' + esc(title) + '</strong> &middot; ' + esc(dateStr) + '</div>' +
          '<div class="wx-web-banner-acts">' +
            (join ? '<a class="wx-web-btn wx-web-btn--primary" href="' + esc(join) + '" target="_blank" rel="noopener">Join</a>' : '') +
            '<button type="button" class="wx-web-btn" data-wx-ics data-ics-title="' + esc(title) + '" data-ics-start="' + esc(startIso) + '" data-ics-mins="' + esc(mins) + '" data-ics-url="' + esc(join) + '" data-ics-desc="' + esc(desc) + '">Add to calendar</button>' +
            '<button type="button" class="wx-web-banner-x" data-wx-web-dismiss="' + soonestTs + '" aria-label="Dismiss">&times;</button>' +
          '</div>';
        overview.insertBefore(banner, overview.firstChild);
        this._webinarBanner = banner;
      }

      _dismissWebinarBanner(key) {
        try { window.localStorage.setItem('wxWebinarDismiss:' + key, '1'); } catch (e) {}
        if (this._webinarBanner) { this._webinarBanner.remove(); this._webinarBanner = null; }
      }

      /* ---------- Articles: submit + list ---------- */
      _loadArticles() {
        if (this.dataset.loggedIn !== '1') return;
        var self = this;
        this._pfetch('/articles', { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) { self._renderArticles(d ? (d.submissions || []) : null); })
          .catch(function () { self._renderArticles(null); });
      }

      _renderArticles(list) {
        var box = this.querySelector('[data-wx-art-list]');
        if (!box) return;
        if (list === null) { box.innerHTML = '<p class="wx-empty">We couldn’t load your submissions.</p>'; return; }
        if (!list.length) { box.innerHTML = '<p class="wx-empty">You haven’t submitted any articles yet.</p>'; return; }
        var esc = function (s) { var d = document.createElement('div'); d.textContent = (s == null ? '' : s); return d.innerHTML; };
        box.innerHTML = list.map(function (a) {
          var st = (a.status || 'pending').toLowerCase();
          var cls = 'review', label = 'In review';
          if (st === 'approved' || st === 'published') { cls = 'published'; label = 'Published'; }
          else if (st === 'rejected') { cls = 'rejected'; label = 'Not approved'; }
          var date = a.created_at ? new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
          var file = a.file_url ? '<a class="wx-comment-file" href="' + esc(a.file_url) + '" target="_blank" rel="noopener"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M6 3h8l4 4v14H6z"/><path d="M14 3v4h4"/></svg>' + esc(a.file_name || 'Attachment') + '</a>' : '';
          return '<div class="wx-art">' +
            '<div class="wx-art-main"><div class="wx-art-title">' + esc(a.title) + '</div>' +
            (date ? '<div class="wx-art-meta">Submitted ' + esc(date) + '</div>' : '') + file + '</div>' +
            '<div class="wx-art-right">' +
              (a.published_url ? '<a class="wx-art-link" href="' + esc(a.published_url) + '" target="_blank" rel="noopener">View</a>' : '') +
              '<span class="wx-art-status wx-art-status--' + cls + '">' + label + '</span>' +
            '</div></div>';
        }).join('');
      }

      _submitArticle(btn) {
        var f = this.querySelector('[data-wx-art-form]');
        if (!f) return;
        var g = function (k) { var el = f.querySelector('[data-wx-art="' + k + '"]'); return el ? el.value.trim() : ''; };
        var body = Object.assign({ title: g('title'), summary: g('summary'), content: g('content') }, this._attachPayload('articles'));
        if (!body.title || !body.content) { this.notice(this._t('art_missing', 'Please add a title and content.'), 'error'); return; }
        var self = this;
        btn.setAttribute('disabled', '');
        this._pfetch('/articles', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        }).then(function (r) { return r.json().catch(function () { return { ok: false }; }); })
          .then(function (d) {
            btn.removeAttribute('disabled');
            if (d && d.ok) {
              ['title', 'summary', 'content'].forEach(function (k) { var el = f.querySelector('[data-wx-art="' + k + '"]'); if (el) el.value = ''; });
              self._attachClear('articles');
              self.notice(self._t('art_submitted', 'Thanks! Your article was submitted for review.'));
              self._loadArticles();
            } else {
              self.notice((d && d.error) || self._t('art_failed', 'Could not submit your article. Please try again.'), 'error');
            }
          })
          .catch(function () { btn.removeAttribute('disabled'); self.notice(self._t('art_failed', 'Could not submit your article. Please try again.'), 'error'); });
      }

      /* ---------- Community / Norsan feeds ---------- */
      _esc(s) { var d = document.createElement('div'); d.textContent = (s == null ? '' : String(s)); return d.innerHTML; }
      _initials(name) {
        var p = String(name || '?').trim().split(/\s+/);
        return (((p[0] || '')[0] || '') + ((p[1] || '')[0] || '')).toUpperCase() || '?';
      }
      _relTime(iso) {
        var t = Date.parse(iso); if (isNaN(t)) return '';
        var s = Math.max(0, (Date.now() - t) / 1000);
        if (s < 60) return 'just now';
        if (s < 3600) return Math.floor(s / 60) + 'm ago';
        if (s < 86400) return Math.floor(s / 3600) + 'h ago';
        if (s < 604800) return Math.floor(s / 86400) + 'd ago';
        return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }

      _loadFeed(channel) {
        if (this.dataset.loggedIn !== '1') return;
        var self = this;
        this._pfetch('/community?channel=' + encodeURIComponent(channel) + '&limit=20', { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) { self._renderFeed(channel, d ? (d.posts || []) : null); })
          .catch(function () { self._renderFeed(channel, null); });
      }

      /* ---------- Attachments (Shopify Files via App Proxy) ---------- */
      // Pending uploads keyed by attachment scope ('community', 'articles',
      // 'comment:<postId>', …). Cleared once the post/comment/article is sent.
      _attachBox(el) { return el.closest('[data-wx-attach]'); }

      _attachPick(btn) {
        var box = this._attachBox(btn);
        var input = box ? box.querySelector('[data-wx-attach-input]') : null;
        if (input) input.click();
      }

      _attachMsg(box, text, kind) {
        var el = box ? box.querySelector('[data-wx-attach-msg]') : null;
        if (!el) return;
        el.classList.remove('wx-attach-msg--err', 'wx-attach-msg--ok');
        if (!text) { el.hidden = true; el.textContent = ''; return; }
        if (kind) el.classList.add('wx-attach-msg--' + kind);
        el.textContent = text;
        el.hidden = false;
      }

      _attachClear(key) {
        if (this._attachments) delete this._attachments[key];
        var box = this.querySelector('[data-wx-attach="' + key + '"]');
        if (!box) return;
        var input = box.querySelector('[data-wx-attach-input]'); if (input) input.value = '';
        var file = box.querySelector('[data-wx-attach-file]'); if (file) file.hidden = true;
        var pick = box.querySelector('[data-wx-attach-pick]'); if (pick) pick.hidden = false;
        this._attachMsg(box, '', null);
      }

      _attachRemove(btn) {
        var box = this._attachBox(btn);
        if (box) this._attachClear(box.getAttribute('data-wx-attach'));
      }

      // Returns { file_url, file_name } for a scope, or {} when nothing is attached.
      _attachPayload(key) {
        var a = this._attachments && this._attachments[key];
        return a ? { file_url: a.file_url, file_name: a.file_name } : {};
      }

      _attachUpload(input) {
        var box = this._attachBox(input);
        if (!box) return;
        var key = box.getAttribute('data-wx-attach');
        var f = input.files && input.files[0];
        if (!f) return;
        var self = this;
        if (!this._attachments) this._attachments = {};

        if (f.size > 15 * 1024 * 1024) {
          this._attachMsg(box, self._t('att_too_large', 'That file is larger than 15 MB. Please pick a smaller one.'), 'err');
          input.value = '';
          return;
        }

        var pick = box.querySelector('[data-wx-attach-pick]');
        var fileRow = box.querySelector('[data-wx-attach-file]');
        var nameEl = box.querySelector('[data-wx-attach-name]');
        this._attachMsg(box, 'Uploading…', null);
        if (pick) pick.hidden = true;
        if (nameEl) nameEl.textContent = f.name;
        if (fileRow) fileRow.hidden = false;

        var mime = f.type || 'application/octet-stream';
        var fail = function (msg) {
          self._attachMsg(box, msg || self._t('att_failed', 'Upload failed. Please try again.'), 'err');
          if (fileRow) fileRow.hidden = true;
          if (pick) pick.hidden = false;
          input.value = '';
          delete self._attachments[key];
        };

        this._pfetch('/uploads/sign', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ filename: f.name, mimeType: mime, fileSize: f.size })
        }).then(function (r) { return r.json().catch(function () { return { ok: false }; }); })
          .then(function (d) {
            if (!d || !d.ok || !d.target) throw new Error((d && d.error) || self._t('att_start_failed', 'Could not start the upload.'));
            // Post the file straight to Shopify's storage — it never touches our server.
            var form = new FormData();
            (d.target.parameters || []).forEach(function (p) { form.append(p.name, p.value); });
            form.append('file', f);
            return fetch(d.target.url, { method: 'POST', body: form }).then(function (up) {
              if (!up.ok) throw new Error(self._t('att_failed', 'Upload failed. Please try again.'));
              return d.target.resourceUrl;
            });
          })
          .then(function (resourceUrl) {
            return self._pfetch('/uploads/finalize', {
              method: 'POST', credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ resourceUrl: resourceUrl, mimeType: mime, filename: f.name })
            }).then(function (r) { return r.json().catch(function () { return { ok: false }; }); });
          })
          .then(function (d) {
            if (!d || !d.ok || !d.file_url) throw new Error((d && d.error) || self._t('att_failed', 'Upload failed. Please try again.'));
            self._attachments[key] = { file_url: d.file_url, file_name: d.file_name || f.name };
            self._attachMsg(box, self._t('att_attached', 'Attached.'), 'ok');
          })
          .catch(function (err) { fail(err && err.message); });
      }

      _commentHtml(c) {
        var esc = this._esc.bind(this);
        var file = c.file_url ? '<a class="wx-comment-file" href="' + esc(c.file_url) + '" target="_blank" rel="noopener"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M6 3h8l4 4v14H6z"/><path d="M14 3v4h4"/></svg>' + esc(c.file_name || 'Attachment') + '</a>' : '';
        return '<div class="wx-comment">' +
          '<div class="wx-comment-av">' + esc(this._initials(c.author_name)) + '</div>' +
          '<div class="wx-comment-main"><div class="wx-comment-head"><span class="wx-comment-name">' + esc(c.author_name) + '</span>' +
          (c.author_title ? '<span class="wx-comment-title">' + esc(c.author_title) + '</span>' : '') +
          '<span class="wx-comment-time">' + esc(this._relTime(c.created_at)) + '</span></div>' +
          '<div class="wx-comment-body">' + esc(c.body).replace(/\n/g, '<br>') + '</div>' + file + '</div></div>';
      }

      _postCardHtml(p) {
        var esc = this._esc.bind(this);
        var comments = (p.comments || []).map(this._commentHtml.bind(this)).join('');
        var moreCount = (p.comment_count || 0) - ((p.comments || []).length);
        var more = moreCount > 0 ? '<button type="button" class="wx-feed-morebtn" data-wx-feed-more="' + esc(p.id) + '">View all ' + esc(p.comment_count) + ' comments</button>' : '';
        var file = p.file_url ? '<a class="wx-post-file" href="' + esc(p.file_url) + '" target="_blank" rel="noopener" download><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M6 3h8l4 4v14H6z"/><path d="M14 3v4h4"/></svg>' + esc(p.file_name || 'Attachment') + '</a>' : '';
        return '<article class="wx-post" data-post-id="' + esc(p.id) + '">' +
          (p.pinned ? '<span class="wx-post-pin">Pinned</span>' : '') +
          '<div class="wx-post-head"><div class="wx-post-av">' + esc(this._initials(p.author_name)) + '</div>' +
          '<div class="wx-post-who"><span class="wx-post-name">' + esc(p.author_name) + '</span>' +
          (p.author_title ? '<span class="wx-post-title">' + esc(p.author_title) + '</span>' : '') +
          '<span class="wx-post-time">' + esc(this._relTime(p.created_at)) + '</span></div></div>' +
          '<div class="wx-post-body">' + esc(p.body).replace(/\n/g, '<br>') + '</div>' + file +
          '<div class="wx-post-comments" data-wx-comments>' + more + comments + '</div>' +
          // The form doubles as the attachment scope so [data-wx-attach-*] lookups resolve.
          '<form class="wx-comment-form" data-wx-attach="comment:' + esc(p.id) + '" onsubmit="return false;">' +
            '<input type="file" data-wx-attach-input hidden accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx">' +
            '<input class="wx-comment-input" data-wx-comment-input type="text" maxlength="2000" placeholder="Write a comment…">' +
            '<button type="button" class="wx-comment-attach" data-wx-attach-pick title="Attach file" aria-label="Attach file">' +
              '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.4 11.05 12.25 20.2a5.5 5.5 0 0 1-7.78-7.78l9.19-9.19a3.67 3.67 0 1 1 5.18 5.18l-9.2 9.19a1.83 1.83 0 1 1-2.59-2.59l8.49-8.48"/></svg>' +
            '</button>' +
            '<button type="button" class="wx-comment-send" data-wx-feed-comment="' + esc(p.id) + '">Send</button>' +
            '<div class="wx-attach-file wx-attach-file--inline" data-wx-attach-file hidden><span class="wx-attach-name" data-wx-attach-name></span><button type="button" class="wx-attach-remove" data-wx-attach-remove aria-label="Remove attachment">&times;</button></div>' +
            '<p class="wx-attach-msg" data-wx-attach-msg hidden role="status"></p>' +
          '</form>' +
          '</article>';
      }

      _renderFeed(channel, posts) {
        var self = this;
        var box = this.querySelector('[data-wx-feed="' + channel + '"]');
        if (!box) return;
        if (posts === null) { box.innerHTML = '<div class="wx-panel"><p class="wx-empty">' + self._t('feed_failed', 'We couldn’t load this feed. Please refresh.') + '</p></div>'; return; }
        if (!posts.length) {
          box.innerHTML = '<div class="wx-panel"><p class="wx-empty">' + (channel === 'norsan' ? self._t('feed_none_norsan', 'No posts from Norsan yet.') : self._t('feed_none_community', 'No posts yet — be the first to share something.')) + '</p></div>';
          return;
        }
        box.innerHTML = '<div class="wx-panel wx-feed-list">' + posts.map(function (p) { return self._postCardHtml(p); }).join('') + '</div>';
      }

      _feedPost(channel) {
        var composer = this.querySelector('[data-wx-feed-composer="' + channel + '"]');
        if (!composer) return;
        var input = composer.querySelector('[data-wx-feed-input]');
        var btn = composer.querySelector('[data-wx-feed-post]');
        var body = input ? input.value.trim() : '';
        var att = this._attachPayload(channel);
        if (!body && !att.file_url) return;
        var self = this;
        if (btn) btn.setAttribute('disabled', '');
        this._pfetch('/community/posts', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(Object.assign({ body: body }, att))
        }).then(function (r) { return r.json().catch(function () { return { ok: false }; }); })
          .then(function (d) {
            if (btn) btn.removeAttribute('disabled');
            if (d && d.ok) { if (input) input.value = ''; self._attachClear(channel); self._loadFeed(channel); }
            else self.notice((d && d.error) || self._t('feed_post_failed', 'Could not post. Please try again.'), 'error');
          })
          .catch(function () { if (btn) btn.removeAttribute('disabled'); self.notice(self._t('feed_post_failed', 'Could not post. Please try again.'), 'error'); });
      }

      _feedComment(btn) {
        var postId = btn.getAttribute('data-wx-feed-comment');
        var form = btn.closest('.wx-comment-form');
        var input = form ? form.querySelector('[data-wx-comment-input]') : null;
        var body = input ? input.value.trim() : '';
        var att = this._attachPayload('comment:' + postId);
        if ((!body && !att.file_url) || !postId) return;
        var card = btn.closest('.wx-post');
        var feed = card ? card.closest('[data-wx-feed]') : null;
        var channel = feed ? feed.getAttribute('data-wx-feed') : null;
        var self = this;
        btn.setAttribute('disabled', '');
        this._pfetch('/community/comments', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(Object.assign({ post_id: postId, body: body }, att))
        }).then(function (r) { return r.json().catch(function () { return { ok: false }; }); })
          .then(function (d) {
            btn.removeAttribute('disabled');
            if (d && d.ok) { if (input) input.value = ''; self._attachClear('comment:' + postId); if (channel) self._loadFeed(channel); }
            else self.notice((d && d.error) || self._t('feed_comment_failed', 'Could not add comment.'), 'error');
          })
          .catch(function () { btn.removeAttribute('disabled'); self.notice(self._t('feed_comment_failed', 'Could not add comment.'), 'error'); });
      }

      _feedMore(postId) {
        var self = this;
        var card = this.querySelector('.wx-post[data-post-id="' + postId + '"]');
        this._pfetch('/community/posts/' + encodeURIComponent(postId) + '/comments', { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) {
            if (!d || !card) return;
            var wrap = card.querySelector('[data-wx-comments]');
            if (wrap) wrap.innerHTML = (d.comments || []).map(self._commentHtml.bind(self)).join('');
          })
          .catch(function () {});
      }

      notice(msg, variant) {
        var el = this.querySelector('[data-wx-notice]');
        if (!el) return;
        el.textContent = msg;
        if (variant) el.setAttribute('data-variant', variant); else el.removeAttribute('data-variant');
        el.hidden = false;
      }

      /* ---------- Order Products: local selection cart ---------- */
      _initOrderCart() {
        this.cartEl = this.querySelector('[data-wx-cart]');
        if (!this.cartEl) return;
        this.selection = this._loadSelection();
        this._renderCart();
      }

      _loadSelection() {
        try {
          var raw = window.localStorage.getItem('wubsExpertOrderV1');
          var arr = raw ? JSON.parse(raw) : [];
          return Array.isArray(arr) ? arr : [];
        } catch (e) { return []; }
      }

      _persist() {
        try { window.localStorage.setItem('wubsExpertOrderV1', JSON.stringify(this.selection)); } catch (e) {}
      }

      _rowQtyStep(btn) {
        var row = btn.closest('[data-wx-prow]');
        if (!row) return;
        var input = row.querySelector('[data-wx-qty]');
        if (!input) return;
        var delta = parseInt(btn.getAttribute('data-wx-qstep'), 10) || 0;
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
      }

      _addFromRow(btn) {
        var row = btn.closest('[data-wx-prow]');
        if (!row) return;
        var vid = btn.getAttribute('data-vid');
        var price = parseInt(btn.getAttribute('data-price'), 10) || 0;
        var title = btn.getAttribute('data-title') || 'Product';
        var image = btn.getAttribute('data-image') || '';

        var breaksJson = btn.getAttribute('data-breaks') || '[]';
        var sel = row.querySelector('[data-wx-variant]');
        if (sel && sel.options.length) {
          var opt = sel.options[sel.selectedIndex];
          vid = opt.value;
          price = parseInt(opt.getAttribute('data-price'), 10) || price;
          title = opt.getAttribute('data-title') || title;
          // Each variant carries its own ladder; the button's copy is only the default variant's.
          if (opt.getAttribute('data-breaks')) breaksJson = opt.getAttribute('data-breaks');
        }
        var breaks = [];
        try { breaks = JSON.parse(breaksJson); } catch (e) { breaks = []; }

        var qtyInput = row.querySelector('[data-wx-qty]');
        var qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);

        this._cartAdd({ vid: String(vid), title: title, price: price, image: image, qty: qty, breaks: breaks });
        this._flash(btn, 'Added');
        this._announce(title + ' added to your order');
      }

      _cartAdd(item) {
        var existing = this.selection.filter(function (i) { return i.vid === item.vid; })[0];
        if (existing) existing.qty += item.qty;
        else this.selection.push(item);
        this._persist();
        this._renderCart();
      }

      _cartStep(vid, delta) {
        var item = this.selection.filter(function (i) { return i.vid === vid; })[0];
        if (!item) return;
        item.qty += delta;
        if (item.qty < 1) { this._cartRemove(vid); return; }
        this._persist();
        this._renderCart();
      }

      _cartRemove(vid) {
        this.selection = this.selection.filter(function (i) { return i.vid !== vid; });
        this._persist();
        this._renderCart();
      }

      _formatMoney(cents) {
        return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }

      _unitPrice(item) {
        var p = item.price;
        if (item.breaks && item.breaks.length) {
          item.breaks.forEach(function (b) { if (item.qty >= b.q) p = b.p; });
        }
        return p;
      }

      _renderCart() {
        if (!this.cartEl) return;
        var itemsEl = this.cartEl.querySelector('[data-wx-cart-items]');
        var emptyEl = this.cartEl.querySelector('[data-wx-cart-empty]');
        var subEl = this.cartEl.querySelector('[data-wx-cart-subtotal]');
        var countEl = this.cartEl.querySelector('[data-wx-cart-count]');
        var checkoutBtn = this.cartEl.querySelector('[data-wx-checkout]');
        var self = this;

        itemsEl.textContent = '';
        var subtotal = 0, count = 0;
        this.selection.forEach(function (i) {
          subtotal += self._unitPrice(i) * i.qty;
          count += i.qty;
          itemsEl.appendChild(self._itemNode(i));
        });

        var empty = this.selection.length === 0;
        emptyEl.hidden = !empty;
        subEl.textContent = empty ? '—' : this._formatMoney(subtotal);
        if (countEl) { countEl.hidden = empty; countEl.textContent = String(count); }
        if (checkoutBtn) checkoutBtn.disabled = empty;
      }

      _itemNode(i) {
        var row = document.createElement('div');
        row.className = 'wx-ci';

        if (i.image) {
          var img = document.createElement('img');
          img.className = 'wx-ci-img'; img.src = i.image; img.alt = ''; img.width = 40; img.height = 40; img.loading = 'lazy';
          row.appendChild(img);
        } else {
          var ph = document.createElement('span'); ph.className = 'wx-ci-img'; row.appendChild(ph);
        }

        var unit = this._unitPrice(i);
        var mid = document.createElement('div');
        var t = document.createElement('div'); t.className = 'wx-ci-title'; t.textContent = i.title; mid.appendChild(t);
        var p = document.createElement('div'); p.className = 'wx-ci-price'; p.textContent = this._formatMoney(unit) + ' each'; mid.appendChild(p);
        if (unit < i.price) {
          var tier = document.createElement('div'); tier.className = 'wx-ci-tier'; tier.textContent = this._t('order_volume', 'Volume price applied'); mid.appendChild(tier);
        }

        var qty = document.createElement('div'); qty.className = 'wx-ci-qty';
        qty.appendChild(this._stepBtn('−', i.vid, -1, 'Decrease quantity'));
        var qn = document.createElement('span'); qn.className = 'wx-ci-qn'; qn.textContent = String(i.qty); qty.appendChild(qn);
        qty.appendChild(this._stepBtn('+', i.vid, 1, 'Increase quantity'));
        mid.appendChild(qty);
        row.appendChild(mid);

        var right = document.createElement('div'); right.className = 'wx-ci-right';
        var line = document.createElement('div'); line.className = 'wx-ci-line'; line.textContent = this._formatMoney(unit * i.qty); right.appendChild(line);
        var rm = document.createElement('button'); rm.type = 'button'; rm.className = 'wx-ci-remove';
        rm.setAttribute('data-wx-remove', ''); rm.setAttribute('data-vid', i.vid); rm.textContent = 'Remove';
        right.appendChild(rm);
        row.appendChild(right);

        return row;
      }

      _stepBtn(label, vid, delta, aria) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'wx-ci-step';
        b.setAttribute('data-wx-step', String(delta)); b.setAttribute('data-vid', vid);
        b.setAttribute('aria-label', aria); b.textContent = label;
        return b;
      }

      _flash(btn, text) {
        var target = btn.querySelector('.btn-text') || btn;
        var orig = btn.dataset._orig || target.textContent.trim();
        btn.dataset._orig = orig;
        target.textContent = text;
        clearTimeout(btn._t);
        btn._t = setTimeout(function () { target.textContent = orig; }, 1100);
      }

      _checkout(btn) {
        if (!this.selection.length) return;
        var self = this;
        var target = btn.querySelector('.btn-text') || btn;
        btn.disabled = true; target.textContent = 'Working…';

        var items = this.selection.map(function (i) { return { id: Number(i.vid), quantity: i.qty }; });
        var post = function (url, body) {
          return fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: body ? JSON.stringify(body) : '{}'
          });
        };

        post('/cart/clear.js')
          .then(function () { return post('/cart/add.js', { items: items }); })
          .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
          .then(function () {
            try { window.localStorage.removeItem('wubsExpertOrderV1'); } catch (e) {}
            window.location.href = '/checkout';
          })
          .catch(function () {
            btn.disabled = false; target.textContent = 'Checkout';
            self._announce(self._t('order_checkout_failed', 'Something went wrong preparing checkout. Please try again.'));
          });
      }
    }
  );
})();
